import React, { useMemo } from "react";
import { useProject } from "../../../../api/useProjects";
import { useProjectSource } from "../../../../api/useSources";
import {
  aggregationIntervalFromPeriod,
  aggregationIntervalToDateValue,
  aggregationIntervalWithDurationFromDateValue,
  mapIntervalFromEntities,
  type AggregationPeriod,
} from "../../../../domain/aggregation/period";
import useListPowerOutputs from "../../../../api/useListPowerOutputs";
import { useOptions } from "./useOptions";
import { css, cx } from "@linaria/core";
import { colors, fonts, spacings } from "@ui/Theme";
import HighchartsComponent from "@ui/Highcharts";
import WidgetCard from "../WidgetCard";
import t from "../../../../i18n";
import type { PointOptionsObject } from "highcharts";
import { useMetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import { useSettings } from "../../../../api/useSettings";
import { getMeasureSeries } from "./useSeries";
import WidgetSelectorHeader, { widgetSelectorHeader } from "../WidgetSelectorHeader";
import NoDataPeriodPlaceholderWidget from "../NoDataPeriodPlaceholderWidget";
import Marker from "@ui/Highcharts/ui/Marker";
import { PointSymbol } from "@ui/Highcharts/ui/PointSymbol";
import seriesColors from "../../Chart/series/seriesColors";
import { WidgetLegend } from "../WidgetLegend";
import useGroups from "../../../../api/useGroups";
import { eventAggregationPeriodPresets } from "../../../../domain/aggregation/preset";
import { useActivePowerPerformanceIndicator } from "../../../../api/usePerformanceIndicators";
import { useAggregationIntervalFromPeriodState } from "../useAggregationIntervalFromPeriodState";
import { ArrowRightUpLine } from "@ui/IconV2/react";
import Link from "@ui/Link";
import { styled } from "@linaria/react";
import { toBasenamePath, useBasename } from "../../../../router/helpers/navigation";
import { toPath } from "../../../../router/helpers/path";
import usePostLatestAnalysisPlotBand from "../../Chart/hooks/usePostLatestAnalysisPlotBand";

const ProductionMonitoringWidget = ({
  projectId,
  projectTimezone,
  className,
  disablePeriod = false,
  hideLink = true,
}: {
  projectId: string;
  projectTimezone: string;
  className?: string;
  disablePeriod?: boolean;
  hideLink?: boolean;
}) => {
  const settings = useSettings();
  const settingsAggregationPeriod = settings.AGGREGATION_PERIOD as AggregationPeriod;
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const chartRef = React.useRef<Highcharts.Chart>(null);

  // Should always return a project
  const project = useProject(projectId) ?? null;
  const source = useProjectSource(project) ?? null;
  if (!project || !source) throw new Error("Project or source not found");

  // Find power quantity
  const activePowerPerformanceIndicator = useActivePowerPerformanceIndicator(projectId);
  const yAxisId = useMemo(
    () =>
      `project-${projectId}-plant-state-production-monitoring-${activePowerPerformanceIndicator?.id}`,
    [projectId, activePowerPerformanceIndicator],
  );

  const groups = useGroups(project.id);
  const aggregationPeriod = useMemo(() => {
    if (settingsAggregationPeriod === "CYCLE" || settingsAggregationPeriod === "CYCLE_2") {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
        intervals: mapIntervalFromEntities(groups),
      });
    } else if (settingsAggregationPeriod === "ALL") {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
        limit: {
          from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
          to: new Date(),
        },
      });
    } else {
      return aggregationIntervalFromPeriod({
        period: settingsAggregationPeriod,
        timezone: project.timezone,
      });
    }
  }, [groups, settingsAggregationPeriod, project]);

  const [aggregationInterval, setAggregationInterval] = useAggregationIntervalFromPeriodState({
    aggregationPeriod: settingsAggregationPeriod,
    projectId,
    projectTimezone,
    shared: false,
    from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
  });
  const aggregationPeriodPresets = useMemo(
    () =>
      eventAggregationPeriodPresets({
        timezone: projectTimezone,
        groups,
        limit: {
          from: new Date(project.analysesAggregate?.earliestActiveAnalysis?.date ?? Date.now()),
          to: new Date(),
        },
      }),
    [groups, project.analysesAggregate?.earliestActiveAnalysis?.date, projectTimezone],
  );

  const basename = useBasename();
  const measuresPath = toBasenamePath(
    toPath("/projects/:projectId/measures", { projectId }),
    basename ?? "/",
  );
  const searchParams = new URLSearchParams();
  if (activePowerPerformanceIndicator) {
    searchParams.set("allData", "1");
    searchParams.set("qoiId", activePowerPerformanceIndicator.id);
  }
  const linkTo = searchParams.toString()
    ? `${measuresPath}?${searchParams.toString()}`
    : measuresPath;

  const powerOutputs = useListPowerOutputs(projectId, aggregationPeriod.from, aggregationPeriod.to);

  const series = useMemo(() => {
    if (!activePowerPerformanceIndicator) return [];
    return [
      getMeasureSeries({
        yAxisId,
        analyses: powerOutputs,
        selectedQuantity: activePowerPerformanceIndicator,
        metrologyOverrides,
      }),
    ];
  }, [yAxisId, activePowerPerformanceIndicator, powerOutputs, metrologyOverrides]);
  const options = useOptions({
    series,
    activePowerUnit: activePowerPerformanceIndicator?.unit ?? "",
    minPeriod: aggregationPeriod.from.getTime(),
    maxPeriod: aggregationPeriod.to.getTime(),
    yAxisId,
    timeZone: project.timezone,
  });

  const hasData =
    powerOutputs.length > 0 &&
    ((series[0]?.data ?? []) as PointOptionsObject[]).filter(
      f => f.x !== undefined && f.y !== undefined,
    ).length > 0;

  usePostLatestAnalysisPlotBand({
    chartRef,
    analyses: powerOutputs,
    labelTextKey: "chart.period.no_data_yet",
  });

  return (
    <WidgetCard className={cx(className, widgetCardWithHeaderMargin)}>
      <WidgetSelectorHeader
        title={t("project.widget.production_monitoring.title")}
        link={
          !hideLink && activePowerPerformanceIndicator ? (
            <StyledLink to={linkTo} target="_blank" rel="noreferrer">
              <ArrowRightUpLine size="s" />
              {t("project.fault_event.live.widget.average_losses.link")}
            </StyledLink>
          ) : null
        }
        aggregationPeriodPresets={aggregationPeriodPresets}
        aggregationIntervalDateValue={aggregationIntervalToDateValue(
          aggregationInterval,
          projectTimezone,
        )}
        onAggregationIntervalChange={interval =>
          setAggregationInterval(
            aggregationIntervalWithDurationFromDateValue(
              interval,
              projectTimezone,
              aggregationPeriodPresets,
            ),
          )
        }
        hidePeriod={false}
        disablePeriod={disablePeriod}
      />
      {activePowerPerformanceIndicator && hasData ? (
        <div className={chart}>
          <HighchartsComponent
            name="Average losses widget"
            ref={chartRef}
            options={options}
            selectablePoints={false}
          />
          <WidgetLegend>
            <Marker name={t("commons.quantities.selectFilter.filters.activation")}>
              <PointSymbol
                height={9}
                width={9}
                fill={seriesColors.sparklineMarker}
                color={seriesColors.sparklineMarker}
              />
            </Marker>
          </WidgetLegend>
        </div>
      ) : (
        <div className={centeredPlaceholder}>
          <NoDataPeriodPlaceholderWidget />
        </div>
      )}
    </WidgetCard>
  );
};
export default ProductionMonitoringWidget;

//#region CSS

const chart = css`
  margin-top: ${spacings.m};
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 76px;
  min-width: 368px;
`;

const centeredPlaceholder = css`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const widgetCardWithHeaderMargin = css`
  .${widgetSelectorHeader} {
    margin-bottom: ${spacings.m};
  }
`;
//#endregion CSS

const StyledLink = styled(Link)`
  display: flex;
  align-items: baseline;
  color: ${colors.lightNitrogen500_Main};
  gap: ${spacings.xxs};
  font: ${fonts.body1};
  :hover {
    text-decoration: underline;
    color: ${colors.lightNitrogen600};
  }

  & > :nth-child(1) {
    align-self: center;
  }
`;
